import { ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { useCallback, useMemo, useRef } from 'react';

import type { DictateOnboardingStepCompletedExtra } from '../../../contracts';
import { getDictationAnalyticsPorts } from '../../../dictation-analytics-ports';
import type { DictateStep } from '../step-registry';
import { toDictateAnalyticsStepName } from './dictateAnalyticsStepName';
import {
  clearAnalyticsSession,
  loadAnalyticsSession,
  saveAnalyticsSession,
} from './onboardingAnalyticsSession';

export const DICTATE_ONBOARDING_SOURCE = 'dictate_onboarding' as const;

type DictateStepCompletedExtra = DictateOnboardingStepCompletedExtra;

/** Lifecycle + entry screen analytics for the desktop dictate onboarding flow. */
export function useDictateOnboardingAnalytics() {
  // Funnel-wide state hydrated once from localStorage so cumulative metrics
  // survive the provider remount caused by the sign-in auth navigation. Without
  // this, a resumed session reports only its post-auth slice.
  const flowStartTimeRef = useRef<number>(0);
  const completedStepsRef = useRef<Set<DictateStep>>(new Set());
  const onboardingStartedRef = useRef<boolean>(false);
  const entryScreenViewedRef = useRef<boolean>(false);
  // Per-step timer is intentionally session-local (step duration, not funnel
  // duration), so it is not persisted.
  const stepStartTimeRef = useRef<number>(Date.now());

  const hydratedRef = useRef(false);
  if (!hydratedRef.current) {
    hydratedRef.current = true;
    const restored = loadAnalyticsSession();
    if (restored) {
      flowStartTimeRef.current = restored.flowStartTimeMs;
      completedStepsRef.current = new Set(restored.completedSteps);
      onboardingStartedRef.current = restored.onboardingStartedTracked;
      entryScreenViewedRef.current = restored.entryScreenViewedTracked;
    }
  }

  const persistSession = useCallback(() => {
    saveAnalyticsSession({
      flowStartTimeMs: flowStartTimeRef.current,
      completedSteps: Array.from(completedStepsRef.current),
      onboardingStartedTracked: onboardingStartedRef.current,
      entryScreenViewedTracked: entryScreenViewedRef.current,
    });
  }, []);

  const resetSession = useCallback(() => {
    flowStartTimeRef.current = 0;
    completedStepsRef.current = new Set();
    onboardingStartedRef.current = false;
    entryScreenViewedRef.current = false;
    clearAnalyticsSession();
  }, []);

  const trackOnboardingStarted = useCallback(
    (params: { resumedFromDisk: boolean }) => {
      // Dedupe across the provider remount that the sign-in auth navigation
      // triggers: the funnel "starts" once, even though the component tree is
      // torn down and rebuilt mid-flow.
      if (onboardingStartedRef.current) return;

      flowStartTimeRef.current = Date.now();
      stepStartTimeRef.current = Date.now();
      onboardingStartedRef.current = true;
      persistSession();

      getDictationAnalyticsPorts().onboarding.trackOnboardingStarted({
        source: DICTATE_ONBOARDING_SOURCE,
        resumed_from_disk: params.resumedFromDisk,
      });
    },
    [persistSession]
  );

  const trackStepCompleted = useCallback(
    (step: DictateStep, stepNumber: number, extra?: DictateStepCompletedExtra) => {
      const durationMs = Date.now() - stepStartTimeRef.current;
      completedStepsRef.current.add(step);
      stepStartTimeRef.current = Date.now();
      persistSession();

      getDictationAnalyticsPorts().onboarding.trackStepCompleted({
        step: toDictateAnalyticsStepName(step),
        step_number: stepNumber,
        duration_ms: durationMs,
        source: DICTATE_ONBOARDING_SOURCE,
        ...extra,
      });
    },
    [persistSession]
  );

  const trackStepSkipped = useCallback((step: DictateStep) => {
    stepStartTimeRef.current = Date.now();

    getDictationAnalyticsPorts().onboarding.trackStepSkipped({
      step_name: toDictateAnalyticsStepName(step),
      source: DICTATE_ONBOARDING_SOURCE,
    });
  }, []);

  const trackOnboardingCompleted = useCallback(() => {
    const totalDurationMs =
      flowStartTimeRef.current === 0 ? 0 : Date.now() - flowStartTimeRef.current;

    getDictationAnalyticsPorts().onboarding.trackOnboardingCompleted({
      source: DICTATE_ONBOARDING_SOURCE,
      total_duration_ms: totalDurationMs,
      steps_completed: completedStepsRef.current.size,
    });

    // The funnel is over: drop the persisted session so a re-entry starts fresh.
    resetSession();
  }, [resetSession]);

  const resetStepTimer = useCallback(() => {
    stepStartTimeRef.current = Date.now();
  }, []);

  const trackScreenViewed = useCallback(
    (screenName: ScreenName) => {
      // Dedupe the entry screen across the provider remount that sign-in auth
      // navigation triggers — same lifecycle as `trackOnboardingStarted`.
      if (screenName === ScreenName.DictateOnboarding) {
        if (entryScreenViewedRef.current) return;

        entryScreenViewedRef.current = true;
        persistSession();
      }

      getDictationAnalyticsPorts().onboarding.trackScreenViewed({
        screen_name: screenName,
      });
    },
    [persistSession]
  );

  return useMemo(
    () => ({
      trackOnboardingStarted,
      trackStepCompleted,
      trackStepSkipped,
      trackOnboardingCompleted,
      resetStepTimer,
      trackScreenViewed,
    }),
    [
      trackOnboardingStarted,
      trackStepCompleted,
      trackStepSkipped,
      trackOnboardingCompleted,
      resetStepTimer,
      trackScreenViewed,
    ]
  );
}

export type UseDictateOnboardingAnalyticsReturn = ReturnType<
  typeof useDictateOnboardingAnalytics
>;
